/**
 * FLY SINGLE MISSION's two standing pickers — `WHO WILL YOU FLY FOR?` and
 * START POSITION. Split from `missions-model.ts` when the historic-missions
 * merge pushed it past the size gate (the established sibling-module
 * pattern); the state machine stays there, these are pure dialog builders.
 */

import {
  aircraftLabel,
  formationsFor,
  MAX_FLIGHT_AIRCRAFT,
  MISSION_REGIONS,
  opposingNation,
  REGION_LABELS,
  scoutsFor,
  SKY_CONDITIONS,
  TIMES_OF_DAY,
  WIND_STRENGTHS,
  type MissionSpec,
} from "@red-opus/engine";
import { AERODROME_START_LABELS } from "../../mission/realism-settings.ts";
import { dialogRow, type DialogRow, type ShellDialog } from "./shell-screen.ts";

/** `WHO WILL YOU FLY FOR?` — the same dialog the career's enlistment uses, emblems and all. */
export function sidePickerDialog(): ShellDialog {
  return {
    title: "WHO WILL YOU FLY FOR?",
    body: [],
    rows: [
      dialogRow("german", "German Air Service", { kind: "command", command: "side:german" }, "cross"),
      dialogRow("allied", "Royal Flying Corps", { kind: "command", command: "side:allied" }, "roundel"),
      dialogRow("cancel", "CANCEL", { kind: "close" }),
    ],
    focus: 0,
  };
}

/**
 * `START AT YOUR AERODROME` / `START NEAR THE ACTION` (roadmap 10a) — the two
 * verbatim strings `PS.EXE` carries for this choice, recovered off both the
 * floppy and CD binary (`realism-settings.ts`). The original's own string
 * layout sits this pair directly beside a `CANCEL`, which is exactly the
 * `sidePickerDialog` shape: a picker, not a checkbox, so it is built the same
 * way rather than wedged into the pixel-exact `REALISM PANEL` (`SIMREAL.BOX`
 * has no spare rect — `realism-screen.ts`).
 */
export function aerodromeStartDialog(current: boolean): ShellDialog {
  return {
    title: "START POSITION",
    body: [],
    rows: [
      dialogRow("on", AERODROME_START_LABELS.on, { kind: "command", command: "aerodrome-start:on" }),
      dialogRow("off", AERODROME_START_LABELS.off, { kind: "command", command: "aerodrome-start:off" }),
      dialogRow("cancel", "CANCEL", { kind: "close" }),
    ],
    focus: current ? 0 : 1,
  };
}

/**
 * The nine boxed values a briefing or a roster screen can open a picker on.
 *
 * `formation` and `total` are the two Flight Assignments fields that had been
 * drawn and wired to nothing since AD-011 — the note there called them "a clean
 * extension point for a follow-up lane", and Dogfight a Squadron is the mission
 * that needed them. Both come straight off the original's own Mission Setup
 * sentence, which boxes them exactly like the rest: *"Your flight of `[three]`
 * will fly `[Sopwith Camels]`. You will fly in `[echelon]` formation, and use
 * `[regular]` ammunition."* (`PS.EXE`, and measured live in
 * `docs/game/ui-reference/index.md`).
 */
export type MissionParamField =
  | "aircraft"
  | "enemy"
  | "region"
  | "ammo"
  | "wind"
  | "sky"
  | "time"
  | "formation"
  | "total";

const PARAM_FIELDS: readonly MissionParamField[] = [
  "aircraft",
  "enemy",
  "region",
  "ammo",
  "wind",
  "sky",
  "time",
  "formation",
  "total",
];

/** The `open:<field>` command a clicked box or value field carries, or `undefined` for anything else. */
export function paramFieldOf(command: string): MissionParamField | undefined {
  if (!command.startsWith("open:")) return undefined;
  const field = command.slice(5);
  return (PARAM_FIELDS as readonly string[]).includes(field) ? (field as MissionParamField) : undefined;
}

/**
 * `PS.EXE`'s count words, verbatim and complete — the table runs `one`, `two`,
 * `three`, `four` and stops, which is the evidence that a flight is capped at
 * `MAX_FLIGHT_AIRCRAFT` on both sides.
 */
export const FLIGHT_SIZE_WORDS: readonly string[] = ["one", "two", "three", "four"].slice(
  0,
  MAX_FLIGHT_AIRCRAFT,
);

function optionDialog(title: string, rows: readonly DialogRow[]): ShellDialog {
  return { title, body: [], rows: [...rows, dialogRow("cancel", "CANCEL", { kind: "close" })], focus: 0 };
}

/**
 * The popup a boxed value opens — the manual's "menu with your possible
 * options" — built off the currently generated spec so the choices offered
 * (which aeroplanes are period- and side-appropriate) are exactly what the
 * generator itself would have drawn from. `spec` is the mission the model
 * describes *before* this pick, so era and nation come from the draw already
 * in effect, not from the field being opened.
 */
export function parameterDialog(field: MissionParamField, spec: MissionSpec): ShellDialog {
  if (field === "aircraft") {
    const options = scoutsFor(spec.playerNation, spec.era);
    return optionDialog(
      "YOUR AIRCRAFT TYPE",
      options.map((key) => dialogRow(key, aircraftLabel(key), { kind: "command", command: `set:aircraft:${key}` })),
    );
  }
  if (field === "enemy") {
    const options = scoutsFor(opposingNation(spec.playerNation), spec.era);
    return optionDialog(
      "ENEMY AIRCRAFT TYPE",
      options.map((key) => dialogRow(key, aircraftLabel(key), { kind: "command", command: `set:enemy:${key}` })),
    );
  }
  if (field === "region") {
    return optionDialog(
      "WHERE YOU WILL BATTLE",
      MISSION_REGIONS.map((region) => dialogRow(region, REGION_LABELS[region], { kind: "command", command: `set:region:${region}` })),
    );
  }
  if (field === "ammo") {
    return optionDialog("AMMUNITION", [
      dialogRow("regular", "Regular", { kind: "command", command: "set:ammo:regular" }),
      dialogRow("incendiary", "Incendiary", { kind: "command", command: "set:ammo:incendiary" }),
    ]);
  }
  // The two Flight Assignments fields. `FORMATION` offers only what the flight's
  // current size may legally fly — `RBMB.EXE`'s own *"A single plane may only fly
  // in solo formation."* — so the popup can never produce a combination the
  // generator would have to quietly discard. `TOTAL AIRCRAFT IN FLIGHT` offers
  // `PS.EXE`'s entire count vocabulary, which is `one`/`two`/`three`/`four` and
  // nothing longer, in every binary the game ships.
  if (field === "formation") {
    const size = spec.flights.filter((flight) => flight.side === "friendly").length;
    return optionDialog(
      "CHOOSE",
      formationsFor(size).map((entry) => dialogRow(entry, entry, { kind: "command", command: `set:formation:${entry}` })),
    );
  }
  if (field === "total") {
    return optionDialog(
      "CHOOSE",
      FLIGHT_SIZE_WORDS.map((word, index) =>
        dialogRow(String(index + 1), word, { kind: "command", command: `set:total:${index + 1}` }),
      ),
    );
  }
  // The three weather fields (roadmap 11s) share the original's own popup
  // title, `CHOOSE`, and — unlike aircraft/enemy/region/ammo, whose captured
  // wording never survived — the row words themselves are verbatim `PS.EXE`
  // strings the constants already carry lowercase, so no display-label lookup
  // is needed the way `aircraftLabel`/`REGION_LABELS` supply one.
  if (field === "wind") {
    return optionDialog(
      "CHOOSE",
      WIND_STRENGTHS.map((wind) => dialogRow(wind, wind, { kind: "command", command: `set:wind:${wind}` })),
    );
  }
  if (field === "sky") {
    return optionDialog(
      "CHOOSE",
      SKY_CONDITIONS.map((sky) => dialogRow(sky, sky, { kind: "command", command: `set:sky:${sky}` })),
    );
  }
  return optionDialog(
    "CHOOSE",
    TIMES_OF_DAY.map((time) => dialogRow(time, time, { kind: "command", command: `set:time:${time}` })),
  );
}
